import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Autocomplete, Box, Button, Chip, TextField, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { MuiColorInput } from 'mui-color-input';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import 'react-multi-date-picker/styles/colors/green.css';
import 'react-multi-date-picker/styles/layouts/mobile.css';
import { useNavigate, useParams } from 'react-router-dom';
import Accordion from 'src/components/common/Accordin';
import DomainListTableRow from 'src/components/domains/domainListTableRow';
import { GeneralSelectMenu, GeneralTable } from 'src/components/general';
import GeneralDriverTextFiled from 'src/components/general/GeneralDriverTextFiled';
import GeneralUpload from 'src/components/general/GeneralUpload';
import { DOMAIN_FORM } from 'src/constants/queryKeys';
import { DOMAINS_INDEX_TABLE_HEAD } from 'src/constants/tableHeads';
import useTable from 'src/hooks/useTable';
import { networkCreateProject, networkEditProject, networkGetProjectDomainDelete, useFetchProjectDomain } from 'src/network/application';
import { PATH_DASHBOARD } from 'src/routes/paths';
import useAppInfoStore from 'src/store/appInfo';
import useFormsModalStore from 'src/store/forms';
import useProjectInfoStore from 'src/store/project';
import { generateSelectMenuItems } from 'src/utils';
import { FormProvider, RHFSwitch, RHFTextField } from '../../components/hook-form';
import { getDefaultValues } from './defaultValues';
import { schema } from './validationSchema';

// ----------------------------------------------------------------------

export default function ProjectForm({ applicationDetails }) {
  const queryClient = useQueryClient();

  const isEditMode = !!applicationDetails;

  const params = useParams();

  const { enqueueSnackbar } = useSnackbar();
  const useTableOptions = useTable();
  const { dense, page, setPage, order, orderBy, rowsPerPage, selected, onChangePage, onChangeRowsPerPage } = useTableOptions;

  const navigate = useNavigate();
  const { modal, closeModal, setModal } = useFormsModalStore();

  const appSectionDetails = modal?.data;

  const { projectInfo } = useProjectInfoStore();
  const projectId = projectInfo?.ApplicationID;
  const { appInfo } = useAppInfoStore();

  const { data: domains } = useFetchProjectDomain({ ApplicationID: +params?.projectId });

  const [themes, setThemes] = useState([]);
  const [selectedTheme, setSelectedTheme] = useState('');

  const amountTypes = generateSelectMenuItems(appInfo?.AmountType, 'TransactionAmountTypeLable', 'TransactionAmountTypeID');

  const [selectedItems, setSelectedItems] = useState([]);

  const [openAccordions, setOpenAccordions] = useState({});

  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [selectedRow, setSelectedRow] = useState();

  const [appType, setAppType] = useState([]);
  const [selectedAppType, setSelectedAppType] = useState('');

  // ----------------------------------------------------------------------

  const lightTheme = applicationDetails?.Application_Theme_LIGHT ? JSON.parse(applicationDetails?.Application_Theme_LIGHT) : '';
  const darkTheme = applicationDetails?.Application_Theme_LIGHT ? JSON.parse(applicationDetails?.Application_Theme_DARK) : '';

  // LIGHT THEME
  const [lightBgPrimary, setLightBgPrimary] = useState(lightTheme?.lightBgPrimary || '#fff');
  const [lightBgSecondary, setLightBgSecondary] = useState(lightTheme?.lightBgSecondary || '#f8f8f8');
  const [lightTextPrimary, setLightTextPrimary] = useState(lightTheme?.lightTextPrimary || '#000');
  const [lightBorderColor, setLightBorderColor] = useState(lightTheme?.lightBorderColor || '#ccc');
  const [lightSuccessColor, setLightSuccessColor] = useState(lightTheme?.lightSuccessColor || '#16a34a');

  // DARK THEME
  const [darkBgPrimary, setDarkBgPrimary] = useState(darkTheme?.darkBgPrimary || '#151515');
  const [darkBgSecondary, setDarkBgSecondary] = useState(darkTheme?.darkBgSecondary || '#282828');
  const [darkTextPrimary, setDarkTextPrimary] = useState(darkTheme?.darkTextPrimary || '#fff');
  const [darkBorderColor, setDarkBorderColor] = useState(darkTheme?.darkBorderColor || '#282828');
  const [darkSuccessColor, setDarkSuccessColor] = useState(darkTheme?.darkSuccessColor || '#16a34a');

  // ----------------------------------------------------------------------

  const defaultValues = getDefaultValues(applicationDetails);

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const { reset, handleSubmit, formState, setValue, control } = methods;

  const onSubmit = async (values, event) => {
    const themeColorLight = {
      lightBgPrimary,
      lightBgSecondary,
      lightTextPrimary,
      lightBorderColor,
      lightSuccessColor,
    };

    const themeColorDark = {
      darkBgPrimary,
      darkBgSecondary,
      darkTextPrimary,
      darkBorderColor,
      darkSuccessColor,
    };

    const PAYLOAD = {
      ...values,
      DefaultTheme: selectedTheme,
      AllowLoginRegister: values.AllowLoginRegister ? 1 : 0,
      EnableSocial: values.EnableSocial ? 1 : 0,
      ShowMenu: values.ShowMenu ? 1 : 0,
      ApplicationNeedPaymentAUD: values.ApplicationNeedPaymentAUD ? 1 : 0,
      ApplicationNeedPaymentVIDEO: values.ApplicationNeedPaymentVIDEO ? 1 : 0,
      ApplicationNeedPaymentGAME: values.ApplicationNeedPaymentGAME ? 1 : 0,
      ShowHeader: values.ShowHeader ? 1 : 0,
      ShowFooter: values.ShowFooter ? 1 : 0,
      ApplicationShowProfileMenu: values?.ApplicationShowProfileMenu ? 1 : 0,
      ThemeColor_Light: JSON.stringify(themeColorLight),
      ThemeColor_Dark: JSON.stringify(themeColorDark),
      IDSShowAmountTypeInHeader: values?.IDSShowAmountTypeInHeader?.map((item) => item?.id)?.join(','),
      LogoLight: values?.ApplicationIsLogoEnable ? values?.LogoLight : '',
      LogoDark: values?.ApplicationIsLogoEnable ? values?.LogoDark : '',
      IsLogoEnable: values?.ApplicationIsLogoEnable ? 1 : 0,
      HasTheme: values?.ApplicationHasTheme ? 1 : 0,
      ApplicationSearchVIDEO: values?.ApplicationSearchVIDEO ? 1 : 0,
      ApplicationSearchAUD: values?.ApplicationSearchAUD ? 1 : 0,
      ApplicationSearchMusic: values?.ApplicationSearchMusic ? 1 : 0,
      ApplicationSearchGame: values?.ApplicationSearchGame ? 1 : 0,
      ApplicationHasScreenSaver: values?.ApplicationHasScreenSaver ? 1 : 0,
      ApplicationTypeID: selectedAppType,
    };

    try {
      if (isEditMode) {
        const res = await networkEditProject(params.projectId, PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('محصول موردنظر با موفقیت ویرایش شد');
          if (event.nativeEvent.submitter.name === 'save') {
            navigate(PATH_DASHBOARD.general.projects);
            reset();
            await queryClient.invalidateQueries({ queryKey: ['project-list'] });
          }
        } else {
          enqueueSnackbar('خطایی رخ داد !', { variant: 'error' });
        }
      } else {
        const res = await networkCreateProject(PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('محصول با موفقیت ایجاد شد');
          if (event.nativeEvent.submitter.name === 'save') {
            navigate(PATH_DASHBOARD.general.projects);
          }
          reset();
          await queryClient.invalidateQueries({ queryKey: ['project-list'] });
        } else {
          enqueueSnackbar('خطایی رخ داد !', { variant: 'error' });
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const onError = (errors) => {
    Object.keys(errors).forEach((field) => {
      setOpenAccordions((prev) => ({ ...prev, [field]: true }));
    });
  };

  // --------------------------------------------------------------------------------------

  useEffect(() => {
    if (appInfo) {
      const themesList = generateSelectMenuItems(appInfo?.ThemeMode, 'Title', 'Value');
      setThemes([{ title: 'سیستم کاربر', id: 'auto' }, ...themesList]);
      const appType = generateSelectMenuItems(appInfo?.AppType, 'ApplicationTypeTitle', 'ApplicationTypeID');
      setAppType(appType);

      setSelectedTheme();
      setSelectedTheme(applicationDetails?.ApplicationDefaultTheme || 'auto');
    }
  }, [appInfo]);

  useEffect(() => {
    if (applicationDetails?.ApplicationIDSShowAmountTypeInHeader) {
      const selectedIdsString = applicationDetails?.ApplicationIDSShowAmountTypeInHeader;
      const selectedIdsArray = selectedIdsString.split(',').map(Number);
      const selectedItems = generateSelectMenuItems(appInfo?.AmountType, 'TransactionAmountTypeLable', 'TransactionAmountTypeID').filter(
        (item) => selectedIdsArray.includes(item.id)
      );
      setSelectedItems(selectedItems);
    }

    if (applicationDetails?.ApplicationTypeID) {
      setSelectedAppType(applicationDetails?.ApplicationTypeID);
    }
  }, [applicationDetails]);

  const handleSelectionChange = (event, newValue) => setSelectedItems(newValue);

  const handleDeleteRow = (id) => {
    networkGetProjectDomainDelete(id)
      .then((res) => {
        if (res.data?.items?.ResultID == 1) {
          enqueueSnackbar('آیتم موردنظر با موفقیت حذف شد');
          queryClient.invalidateQueries(['project-domain']);
        } else {
          enqueueSnackbar('با مشکل مواجه شد !', { variant: 'error' });
        }
      })
      .catch((error) => console.log(error))
      .finally(() => {
        setOpenConfirmDelete(false);
      });
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit, onError)}>
      <Accordion title={'تنظیمات محصول'}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
          <GeneralDriverTextFiled
            helperDesc={`اگه می‌خوای ترافیک و عملکرد محصول‌ات رو در گوگل آنالیتیکس ردیابی کنی، کد مربوط به گوگل آنالیتیکس رو اینجا وارد کن.`}
            name="GA"
            type="text"
            label="گوگل آنالیتیکس"
          />

          <GeneralDriverTextFiled
            helperDesc={`اگه محصول به یه بازی مرتبطه یا شامل یه بازیه، لینک بازی رو اینجا وارد کن.`}
            name="LinkPlayGame"
            type="text"
            label="لینک بازی"
          />
          <GeneralDriverTextFiled
            helperDesc={`اگه محصول‌ات شامل محتوای مدیا مثل عکس یا ویدیو هست، لینک اون‌ها رو اینجا قرار بده تا بتونیم به راحتی دسترسی داشته باشیم.`}
            name="LinkPlayMedia"
            type="text"
            label="لینک مدیا"
          />

          <GeneralDriverTextFiled
            helperDesc={`اگه محصول‌ات شامل یک فروشگاه آنلاینه، لینک صفحه فروشگاه رو اینجا وارد کن. این لینک به مشتری‌ها اجازه می‌ده تا مستقیم به صفحه فروشگاه برن و محصولات رو ببینن و خرید کنن.`}
            name="LinkListPackage"
            type="text"
            label="لینک صفحه فروشگاه"
          />
          <GeneralDriverTextFiled
            helperDesc={`در صورتی که محصول بخشی داره که کاربران نیاز دارن عضو بشن، لینک عضویت رو اینجا وارد کن تا کاربران بتونن به راحتی عضو بشن.`}
            name="LinkListSubscribe"
            type="text"
            label="لینک عضویت"
          />

          <Controller
            name="IDSShowAmountTypeInHeader"
            control={control}
            defaultValue={selectedItems}
            render={({ field }) => (
              <Autocomplete
                {...field}
                multiple
                freeSolo
                onChange={(event, newValue) => {
                  handleSelectionChange(event, newValue);
                  field.onChange(newValue);
                }}
                options={amountTypes}
                getOptionLabel={(option) => option?.title || ''}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                renderTags={(value, getTagProps) =>
                  Array.isArray(value) &&
                  value.map((option, index) => <Chip key={option?.id} label={option?.title} {...getTagProps({ index })} />)
                }
                renderInput={(params) => <TextField label="ارزهایی که در هدر محصول نمایش داده میشود" {...params} />}
                value={selectedItems}
              />
            )}
          />

          <GeneralDriverTextFiled
            helperDesc={
              <div className="flex flex-col gap-1">
                <p>
                  این فیلد برای وارد کردن متنی استفاده می‌شه که به عنوان placeholder یا متن راهنما داخل باکس اینپوت نظرات (نظرات) نمایش داده
                  می‌شه. این متن به کاربران نشون می‌ده که چی باید داخل باکس بنویسن.
                </p>
                <img src="/images/screenshots/comment-placeholder.png" alt="helper" />
              </div>
            }
            name="CommentInputBoxPlaceHolder"
            type="text"
            label="متن جایگزین باکس اینپوت نظر"
          />

          <GeneralSelectMenu
            label="مدل کسب و کار"
            name="TypeID"
            options={appType}
            value={selectedAppType}
            onChangeValue={setSelectedAppType}
          />

          <RHFTextField name="VAST_URL" type="text" label="آدرس ویدیو تبلیغاتی" />

          <RHFSwitch name="ApplicationHasScreenSaver" labelPlacement="end" label="نمایش screen saver" />
        </div>
        <GeneralDriverTextFiled
          helperDesc={`در این فیلد باید توضیحات مربوط به "تابلوی زمان" یا سیستم مدیریت زمان محصول رو وارد کنی. این متن به کاربران کمک می‌کنه تا با نحوه استفاده از این ابزار و قابلیت‌های مرتبط با مدیریت زمان آشنا بشن.`}
          name="InformationWalletTime"
          multiline
          rows={6}
          label="متن راهنمای تابلوی زمان"
          sx={{ mt: '16px' }}
        />

        <Typography
          sx={{
            mt: '16px',
            mb: 2,
            color: (theme) => (theme.palette.mode === 'light' ? 'text.secondary' : 'common.white'),
          }}
        >
          اشتراکی شدن محتوا
        </Typography>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-x-4 gap-y-3">
          <RHFSwitch name="ApplicationNeedPaymentAUD" labelPlacement="end" label="محتوای صوتی" />
          <RHFSwitch name="ApplicationNeedPaymentVIDEO" labelPlacement="end" label="ویدیو" />
          <RHFSwitch name="ApplicationNeedPaymentGAME" labelPlacement="end" label="بازی" />
        </div>

        <Typography
          sx={{
            mt: '16px',
            mb: 2,
            color: (theme) => (theme.palette.mode === 'light' ? 'text.secondary' : 'common.white'),
          }}
        >
          جستجو بر اساس
        </Typography>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-x-4 gap-y-3">
          <RHFSwitch name="ApplicationSearchVIDEO" labelPlacement="end" label="ویدیو" />
          <RHFSwitch name="ApplicationSearchAUD" labelPlacement="end" label="کتاب صوتی" />
          <RHFSwitch name="ApplicationSearchMusic" labelPlacement="end" label="موسیقی" />
          <RHFSwitch name="ApplicationSearchGame" labelPlacement="end" label="بازی" />
        </div>

        <Typography
          sx={{
            mt: '16px',
            mb: 2,
            color: (theme) => (theme.palette.mode === 'light' ? 'text.secondary' : 'common.white'),
          }}
        >
          نمایش اقسام محصول
        </Typography>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-x-4 gap-y-3">
          <RHFSwitch name="EnableSocial" labelPlacement="end" label="فعالیت های اجتماعی" />
          <RHFSwitch name="ShowMenu" labelPlacement="end" label="منو" />
          <RHFSwitch name="ShowHeader" labelPlacement="end" label="هدر" />
          <RHFSwitch name="ShowFooter" labelPlacement="end" label="فوتر" />
          <RHFSwitch name="ApplicationShowProfileMenu" labelPlacement="end" label="منوی پروفایل" />
        </div>
      </Accordion>

      <Accordion title={'اطلاعات ظاهری'}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3 mb-4">
          <GeneralSelectMenu
            label="رنگ پس زمینه"
            name="DefaultTheme"
            options={themes}
            value={selectedTheme}
            onChangeValue={setSelectedTheme}
          />

          <GeneralUpload
            id={'app_backdrop'}
            name="Backdrop"
            label="پس زمینه محصول"
            afterChangeAction={(res) => setValue('Backdrop', res)}
          />

          <div>
            <GeneralUpload
              id={'app_logo_light'}
              name="LogoLight"
              label="لوگو محصول - روشن"
              afterChangeAction={(res) => setValue('LogoLight', res)}
            />

            <Typography
              variant="caption"
              sx={{
                marginTop: '3px',
                color: 'text.secondary',
                fontSize: '0.9rem',
              }}
            >
              حداکثر عرض: 120px - حداکثر ارتفاع: 40px
            </Typography>
          </div>

          <div>
            <GeneralUpload
              id={'app_logo_dark'}
              name="LogoDark"
              label="لوگو محصول - تیره"
              afterChangeAction={(res) => setValue('LogoDark', res)}
            />

            <Typography
              variant="caption"
              sx={{
                marginTop: '3px',
                color: 'text.secondary',
                fontSize: '0.9rem',
              }}
            >
              حداکثر عرض: 120px - حداکثر ارتفاع: 40px
            </Typography>
          </div>

          <RHFSwitch name="ApplicationIsLogoEnable" labelPlacement="end" label="لوگو نمایش داده شود؟" sx={{ my: 2 }} />
          <RHFSwitch name="ApplicationHasTheme" labelPlacement="end" label="آیکون روز و شب نمایش داده شود؟" sx={{ my: 2 }} />
        </div>

        <Typography
          sx={{
            mb: 2,
            color: (theme) => (theme.palette.mode === 'light' ? 'text.secondary' : 'common.white'),
          }}
        >
          تم روشن
        </Typography>
        <Box
          sx={{
            mt: 3,
            display: 'grid',
            columnGap: 2,
            rowGap: 3,
            gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)', lg: 'repeat(5, 1fr)' },
            width: '100%',
          }}
        >
          <MuiColorInput format="HEX" label="Bg-Primary" value={lightBgPrimary} onChange={(color) => setLightBgPrimary(color)} />
          <MuiColorInput format="HEX" label="Bg-Secondary" value={lightBgSecondary} onChange={(color) => setLightBgSecondary(color)} />
          <MuiColorInput format="HEX" label="Text-Primary" value={lightTextPrimary} onChange={(color) => setLightTextPrimary(color)} />
          <MuiColorInput format="HEX" label="Border-Color" value={lightBorderColor} onChange={(color) => setLightBorderColor(color)} />
          <MuiColorInput format="HEX" label="Success-Color" value={lightSuccessColor} onChange={(color) => setLightSuccessColor(color)} />
        </Box>

        <Typography
          sx={{
            my: 2,
            color: (theme) => (theme.palette.mode === 'light' ? 'text.secondary' : 'common.white'),
          }}
        >
          تم تیره
        </Typography>

        <Box
          sx={{
            mt: 3,
            display: 'grid',
            columnGap: 2,
            rowGap: 3,
            gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)', lg: 'repeat(5, 1fr)' },
            width: '100%',
          }}
        >
          <MuiColorInput format="HEX" label="Bg-Primary" value={darkBgPrimary} onChange={(color) => setDarkBgPrimary(color)} />
          <MuiColorInput format="HEX" label="Bg-Secondary" value={darkBgSecondary} onChange={(color) => setDarkBgSecondary(color)} />
          <MuiColorInput format="HEX" label="Text-Primary" value={darkTextPrimary} onChange={(color) => setDarkTextPrimary(color)} />
          <MuiColorInput format="HEX" label="Border-Color" value={darkBorderColor} onChange={(color) => setDarkBorderColor(color)} />
          <MuiColorInput format="HEX" label="Success-Color" value={darkSuccessColor} onChange={(color) => setDarkSuccessColor(color)} />
        </Box>
      </Accordion>
      <Accordion
        clear={() => setOpenAccordions({})}
        expanded={['Title'].map((fieldItem) => openAccordions[fieldItem] === true).includes(true)}
        title={'اطلاعات تحلیلی / SEO'}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3 mt-4">
          <GeneralDriverTextFiled
            maxCharacters={70}
            name="Title"
            type="text"
            label="عنوان محصول"
            helperDesc={`لطفاً عنوان محصول رو در این فیلد وارد کن. این عنوان باید مشخص و نمایانگر محصول باشه تا در پنل مدیریتی به راحتی قابل شناسایی باشه.`}
          />

          <GeneralDriverTextFiled
            helperDesc={`اینجا باید کد ترکر (Clarity) یا کدی که برای ردیابی تراکنش‌ها و فعالیت‌های محصول استفاده می‌کنی رو وارد کنی. این کدها معمولاً برای تحلیل دقیق‌تر داده‌ها و پیگیری فعالیت‌های کاربران به کار می‌رن.`}
            helperTitle="کد ترکر (Clarity)"
            name="TrackerCode"
            type="text"
            label="کد ترکر"
          />

          <GeneralDriverTextFiled
            name="Desc"
            multiline
            rows={6}
            label="توضیحات"
            maxCharacters={250}
            helperDesc={`در این قسمت، باید توضیحات کامل مربوط به محصول رو وارد کنی. این توضیحات می‌تونه شامل هدف محصول، جزئیات مهم و هر چیزی که باید تیم بدونن باشه.`}
            sx={{ mb: '4px' }}
          />

          <GeneralDriverTextFiled
            helperDesc={`در این فیلد باید متن راهنمای کلی محصول یا سیستم رو وارد کنی. این متن به کاربران کمک می‌کنه تا با فرآیندها و قابلیت‌های محصول بهتر آشنا بشن و به راحتی از امکانات استفاده کنن.`}
            name="Help"
            multiline
            rows={6}
            label="متن راهنما"
            sx={{ mb: '16px' }}
          />

          <GeneralDriverTextFiled
            helperDesc={`اینجا قوانین و مقررات مربوط به استفاده از محصول یا سرویس رو وارد کن. این قوانین معمولاً شامل مواردی مثل حقوق و تعهدات کاربران، شرایط استفاده، و سیاست‌های حفظ حریم خصوصی می‌شه.`}
            name="TermsofPolicy"
            multiline
            rows={6}
            label="قوانین مقررات"
            sx={{ mb: '16px' }}
          />

          <GeneralDriverTextFiled
            helperDesc={`در این فیلد می‌تونی سوالات متداول کاربران و پاسخ‌های مرتبط رو وارد کنی. این بخش به کاربران کمک می‌کنه تا بدون نیاز به پشتیبانی، سریع‌تر به جواب سوالاتشون برسن.`}
            name="QA"
            multiline
            rows={6}
            label="سوالات متداول"
            sx={{ mb: '16px' }}
          />

          <GeneralDriverTextFiled
            helperDesc={`این فیلد برای وارد کردن متنیه که هنگام ورود کاربران به سیستم یا سایت به صورت پنجره بازشو (popup) نمایش داده می‌شه. معمولاً این متن شامل پیام خوشامدگویی و اطلاعات ابتداییه که کاربران باید بدونن.`}
            name="WelcomePopup"
            multiline
            rows={6}
            label="متن پنجره بازشو (popup) خوشامد گویی"
          />
        </div>
      </Accordion>

      <Accordion title={'اطلاعات ورود به محصول'}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
          <GeneralDriverTextFiled
            helperDesc={
              <div className="flex flex-col gap-1">
                <p>
                  اینجا عنوانی رو وارد می‌کنی که در فرم ثبت‌نام نمایش داده می‌شه. این عنوان می‌تونه شامل یک پیام خوشامدگویی یا توضیح کوتاهی
                  باشه که کاربر رو به ثبت‌نام در سیستم دعوت می‌کنه.
                </p>
                <img src="/images/screenshots/register-title.png" alt="helper" />
              </div>
            }
            name="RegisterTitle"
            type="text"
            label="عنوان ثبت نام"
          />

          <GeneralDriverTextFiled
            helperDesc={
              <div className="flex flex-col gap-1">
                <p>
                  اگر محصول‌ات بخشی داره که کاربران نیاز دارن ثبت نام کنن، زیرعنوان مربوط به اون بخش رو اینجا وارد کن تا در فرآیند ثبت نام
                  نمایش داده بشه .
                </p>
                <img src="/images/screenshots/register-subtitle.png" alt="helper" />
              </div>
            }
            name="RegisterSubTitle"
            type="text"
            label="زیرعنوان ثبت نام"
          />

          <GeneralDriverTextFiled
            helperDesc={
              <div className="flex flex-col gap-1">
                <p>
                  اینجا باید عنوانی رو وارد کنی که در فرم ورود به محصول یا سیستم نمایش داده می‌شه. این عنوان می‌تونه یک پیام خوشامدگویی
                  مختصر یا دستورالعمل ورود به سیستم باشه، تا کاربران بهتر متوجه بشن که باید لاگین کنن .
                </p>
                <img src="/images/screenshots/login-title.png" alt="helper" />
              </div>
            }
            name="LoginTitle"
            type="text"
            label="عنوان ورود"
          />

          <GeneralDriverTextFiled
            helperDesc={
              <div className="flex flex-col gap-1">
                <p>مشابه بخش ثبت نام، اگر محصول‌ات شامل فرآیند ورود کاربران هست، این زیرعنوان رو وارد کن تا در بخش ورود نمایش داده بشه.</p>
                <img src="/images/screenshots/login-subtitle.png" alt="helper" />
              </div>
            }
            name="LoginSubTitle"
            type="text"
            label="زیرعنوان ورود"
          />

          <GeneralDriverTextFiled
            helperDesc={`اینجا لینکی رو وارد می‌کنی که کاربران باید قبل از ثبت نام بخونن و باهاش موافقت کنن. این لینک معمولاً به قوانین و مقررات ثبت نام یا شرایط استفاده از سرویس مرتبطه.`}
            name="TermsUrl"
            type="text"
            label="لینک قوانین ثبت نام"
          />

          <div className="*:h-full">
            <RHFSwitch name="AllowLoginRegister" labelPlacement="end" label="ورود / ثبت نام دارد ؟" />
          </div>
        </div>
      </Accordion>

      <div className="flex items-center gap-4 mt-6 mb-10">
        <LoadingButton type="submit" variant="contained" name="save" loading={formState?.isSubmitting}>
          ذخیره
        </LoadingButton>
        <LoadingButton type="submit" variant="contained" color="info" name="saveAndContinue" loading={formState?.isSubmitting}>
          ذخیره و ادامه ویرایش
        </LoadingButton>
        <Button onClick={() => navigate(-1)} variant="contained" color="error">
          انصراف
        </Button>
      </div>
      {isEditMode && (
        <Accordion title={'دامنه های متصل'}>
          <div className="mb-5 pb-5 border-b flex justify-end">
            <LoadingButton
              onClick={() => {
                setModal({
                  data: {
                    isEditMode: false,
                    id: null,
                  },
                  isOpen: true,
                  type: DOMAIN_FORM,
                  title: 'دامنه جدید',
                });
              }}
              type="button"
              variant="contained"
            >
              ثبت دامنه جدید
            </LoadingButton>
          </div>
          <GeneralTable
            tableData={(domains && domains?.items) || []}
            headLabel={DOMAINS_INDEX_TABLE_HEAD}
            hasDeleteOption={false}
            CustomTableRow={DomainListTableRow}
            useTableOptions={useTableOptions}
            rowID={'ApplicationDomainID'}
            onEditRow={(data) => {
              setModal({
                data: {
                  isEditMode: true,
                  id: data?.ApplicationDomainID,
                  ApplicationDomainHostName: data?.ApplicationDomainHostName,
                },
                isOpen: true,
                type: DOMAIN_FORM,
                title: 'ویرایش دامنه',
              });
            }}
            onDeleteRow={handleDeleteRow}
            openConfirmDelete={openConfirmDelete}
            setOpenConfirmDelete={setOpenConfirmDelete}
            selectedRow={selectedRow}
            setSelectedRow={setSelectedRow}
          />
        </Accordion>
      )}
    </FormProvider>
  );
}
